import React, { FC, useState, useCallback, ChangeEvent, useMemo } from 'react'
import makeStyles from '@mui/styles/makeStyles';

import Button from '@mui/material/Button'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import Tab from '@mui/material/Tab'
import FormControl from '@mui/material/FormControl'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import FileInput from '../filestore/FileInput'
import KnowledgebaseArticleChooser from '../knowledgebase/ArticleChooser'

import {
  RawDraftContentState,
} from 'draft-js'

import {
  IData_MagicTag_Type,
} from '@kaidam/fc-shared/src/types/schema/magic_tag'

import {
  ILink_KnowledgeBase_Source,
} from '@kaidam/fc-shared/src/types/knowledgebase'

import {
  IField_Storage_File,
} from '@kaidam/fc-shared/src/types/storage'

import {
  ITextEditor_Link,
  ITextEditor_Link_Target,
  ITextEditor_Link_Type,
  ITextEditor_Link_Type_Group,
} from '@kaidam/fc-shared/src/types/texteditor'

import {
  getRawStateFromString,
} from '@kaidam/fc-shared/src/texteditor/utils'

import {
  IVoidCallback,
} from '@kaidam/fc-shared/src/types/utils'

import {
  TEXT_EDITOR_LINK_GROUP_TITLES,
  TEXT_EDITOR_LINK_TYPE_GROUPS,
  TEXT_EDITOR_LINK_TYPE_TEXT,
  TEXT_EDITOR_LINK_TYPE_FILE,
  TEXT_EDITOR_LINK_TYPE_KNOWLEDGEBASE,
  TEXT_EDITOR_LINK_TYPE_ASTONISH_UNSUBSCRIBE,
  TEXT_EDITOR_LINK_TYPE_ASTONISH_OPEN_BROWSER,
  TEXT_EDITOR_DEFAULT_LINK_TARGET,
  TEXT_EDITOR_LINK_TARGET_TYPES,
  TEXT_EDITOR_LINK_GROUP_SETTINGS,
} from '@kaidam/fc-shared/src/constants'

import MagicStringEditor from './MagicStringEditor'
import Window from '../dialog/Window'


const useStyles = makeStyles(theme => ({
  select: {
    width: '100%',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  container: {
    minHeight: '300px',
  },
  content: {
    padding: theme.spacing(3),
  }
}))

interface LinkEditorProps {
  value?: ITextEditor_Link,
  enabledMagicTags?: IData_MagicTag_Type[],
  linkTypes?: ITextEditor_Link_Type[],
  publicFiles?: boolean,
  sharedFiles?: boolean,
  onSave: {
    (value: ITextEditor_Link): void,
  },
  onReset: IVoidCallback,
  onCancel: IVoidCallback,
}

const cleanLink = (link: ITextEditor_Link): ITextEditor_Link => {
  const returnLink = Object.assign({}, link)
  delete(returnLink.file)
  delete(returnLink.url)
  delete(returnLink.knowledgebase)
  return returnLink
}

const LinkEditor: FC<React.PropsWithChildren<LinkEditorProps>> = ({
  value,
  enabledMagicTags = [],
  linkTypes = [],
  publicFiles = false,
  sharedFiles = false,
  onSave,
  onReset,
  onCancel,
}) => {

  const classes = useStyles()

  // let's just assume all things allow text links shall we?
  // (otherwise we need to think about what the default value is based on linkTypes)
  const [ editValue, setEditValue ] = useState<ITextEditor_Link>(value || {
    type: TEXT_EDITOR_LINK_TYPE_TEXT,
    url: getRawStateFromString('https://'),
    target: TEXT_EDITOR_DEFAULT_LINK_TARGET,
  })

  const [ group, setGroup ] = useState<ITextEditor_Link_Type_Group>(TEXT_EDITOR_LINK_TYPE_GROUPS[editValue.type])

  const activeGroups = useMemo<ITextEditor_Link_Type_Group[]>(() => {
    const linkTypeGroups = linkTypes.reduce<ITextEditor_Link_Type_Group[]>((all, linkType) => {
      const group = TEXT_EDITOR_LINK_TYPE_GROUPS[linkType]
      return all.indexOf(group) >= 0 ? all : all.concat([group])
    }, [])
    return linkTypeGroups.concat([TEXT_EDITOR_LINK_GROUP_SETTINGS])
  }, [
    linkTypes,
  ])

  const groupIndex = useMemo(() => {
    return activeGroups.indexOf(group)
  }, [
    group,
    activeGroups,
  ])

  const onClickTab = useCallback((e: any, index: number) => {
    setGroup(activeGroups[index])
  }, [
    activeGroups,
  ])

  const onUpdateUrl = useCallback((url: RawDraftContentState) => {
    setEditValue(Object.assign({}, cleanLink(editValue), {
      type: TEXT_EDITOR_LINK_TYPE_TEXT,
      url,
    }))
  }, [
    editValue,
  ])

  const onUpdateTarget = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const target = event.target.value as ITextEditor_Link_Target
    setEditValue(Object.assign({}, editValue, {target}))
  }, [
    editValue,
  ])

  // we don't allow to deactivate these otherwise we have a "ghost" link that does nothing
  // they should use the other tabs if they want to de-activate the astonish link types
  const onUpdateAstonishLinkType = useCallback((type: ITextEditor_Link_Type) => {
    setEditValue(Object.assign({}, cleanLink(editValue), {
      type,
    }))
  }, [
    editValue,
  ])

  const onUpdateFileLinkType = useCallback((file: IField_Storage_File | null) => {
    setEditValue(Object.assign({}, cleanLink(editValue), {
      type: TEXT_EDITOR_LINK_TYPE_FILE,
      file,
    }))
  }, [
    editValue,
  ])

  const onUpdateKnowledgebaseType = useCallback((knowledgebase: ILink_KnowledgeBase_Source | null) => {
    setEditValue(Object.assign({}, cleanLink(editValue), {
      type: TEXT_EDITOR_LINK_TYPE_KNOWLEDGEBASE,
      knowledgebase,
    }))
  }, [
    editValue,
  ])

  const onSubmit = useCallback(() => {
    onSave(editValue)
  }, [
    group,
    editValue,
  ])

  return (
    <Window
      open
      size="md"
      compact
      onCancel={ onCancel }
      withCancel
      onSubmit={ onSubmit }
      submitTitle="Save"
      leftButtons={(
        <Button
          variant="contained"
          onClick={ onReset }
        >
          Reset Link
        </Button>

      )}
    >
      <div className={ classes.container }>
        <Tabs
          value={ groupIndex }
          onChange={ onClickTab }
        >
          {
            activeGroups.map((activeGroup) => {
              return (
                <Tab id={activeGroup} key={activeGroup} label={ TEXT_EDITOR_LINK_GROUP_TITLES[activeGroup] } />
              )
            })
          }
        </Tabs>
        <div className={ classes.content }>
          {
            group == 'text' && (
              <>
                <Typography color="textSecondary" gutterBottom>
                  Enter the URL of the page you want to link to.  This can be an external document or website.
                </Typography>
                <Typography color="textSecondary" gutterBottom>
                  You can also use the magic tags button to include values from org profiles.
                </Typography>
                <MagicStringEditor
                  value={ editValue.url }
                  enabled={ enabledMagicTags }
                  onChange={ onUpdateUrl }
                />
              </>
            )
          }
          {
            group == 'astonish' && (
              <>
                <Typography color="textSecondary" gutterBottom>
                  Use these options to turn the link into an email function.
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={ editValue.type == TEXT_EDITOR_LINK_TYPE_ASTONISH_UNSUBSCRIBE }
                      onChange={ (e) => {
                        onUpdateAstonishLinkType(TEXT_EDITOR_LINK_TYPE_ASTONISH_UNSUBSCRIBE)
                      }}
                      name={ TEXT_EDITOR_LINK_TYPE_ASTONISH_UNSUBSCRIBE }
                      color="primary"
                    />
                  }
                  label="Unsubscribe"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={ editValue.type == TEXT_EDITOR_LINK_TYPE_ASTONISH_OPEN_BROWSER }
                      onChange={ (e) => {
                        onUpdateAstonishLinkType(TEXT_EDITOR_LINK_TYPE_ASTONISH_OPEN_BROWSER)
                      }}
                      name={ TEXT_EDITOR_LINK_TYPE_ASTONISH_OPEN_BROWSER }
                      color="primary"
                    />
                  }
                  label="Open in Browser"
                />
              </>
            )
          }
          {
            group == 'settings' && (
              <FormControl className={ classes.select }>
                <Select
                  value={ editValue ? editValue.target : TEXT_EDITOR_DEFAULT_LINK_TARGET }
                  onChange={ onUpdateTarget }
                >
                  {
                    TEXT_EDITOR_LINK_TARGET_TYPES.map(type => (
                      <MenuItem key={ type.type } value={ type.type }>{ type.title }</MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            )
          }
          {
            group == 'file' && (
              <>
                <FileInput
                  chooserActive
                  uploadActive={ false }
                  value={ editValue.file }
                  publicMode={ publicFiles }
                  sharedMode={ sharedFiles }
                  onChange={ onUpdateFileLinkType }
                />
              </>
            )
          }
          {
            group == 'knowledgebase' && (
              <>
                <KnowledgebaseArticleChooser
                  value={ editValue.knowledgebase }
                  onChange={ onUpdateKnowledgebaseType }
                />
              </>
            )
          }
        </div>
      </div>
    </Window>
  )
}

export default LinkEditor